import { report } from './reporter';

/**
 * Server errors arrive as PostgREST error messages of the form `<code>: <detail>`
 * (see app.fail in the migrations). Parse them into a stable shape for the UI. A refusal at the
 * owner boundary (ADR-025) comes the same way, as `forbidden: ...` with HTTP 403: run_command
 * records the attempt, so it answers instead of raising.
 *
 * Those messages are written for staff and are shown as they are. Anything else (a database
 * internal, a timeout, a bug) is never shown raw (ADR-037): staff see a plain sentence, and the
 * original goes to the error reporter, once.
 */
export interface AppError {
  code: string;
  message: string;
  hint?: unknown;
  /** The original text of an error that is not shown as it is. */
  detail?: string;
}

const KNOWN = new Set([
  'network',
  'unauthenticated',
  'forbidden',
  'invalid_payload',
  'not_found',
  'conflict',
  'command_conflict',
  'session_required',
  'session_expired',
  'terminal_revoked',
  'employee_suspended',
  'pin_locked',
  'pin_invalid',
  'mfa_required',
  'mfa_setup_required',
  'reauth_required',
]);

export const NETWORK_MESSAGE =
  'No connection to the server. Try again when the connection is back.';
export const UNKNOWN_MESSAGE =
  'Something went wrong. Try again; if it keeps happening, tell the owner.';
const TIMEOUT_MESSAGE = 'The server took too long to answer. Try again.';
const EXPIRED_MESSAGE = 'Your sign-in has expired. Sign in again.';

/** Supabase Auth's refusals ("Email not confirmed") are written for the person signing in; its own
 * server faults (5xx) are not, and neither are its network errors. */
const AUTH_REFUSALS = new Set([
  'AuthApiError',
  'AuthWeakPasswordError',
  'AuthInvalidCredentialsError',
]);
function isAuthRefusal(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const { name, status } = err as { name?: unknown; status?: unknown };
  return (
    typeof name === 'string' &&
    AUTH_REFUSALS.has(name) &&
    typeof status === 'number' &&
    status >= 400 &&
    status < 500
  );
}

const reported = new WeakSet<object>();
function reportOnce(err: unknown, raw: string) {
  if (err && typeof err === 'object') {
    if (reported.has(err)) return;
    reported.add(err);
  }
  report().captureException(err instanceof Error ? err : new Error(raw), { source: 'app-error' });
}

export function toAppError(err: unknown): AppError {
  const raw =
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
      ? (err as { message: string }).message
      : typeof err === 'string'
        ? err
        : null;
  if (raw !== null) {
    const m = /^([a-z_]+):\s*([\s\S]*)$/.exec(raw);
    if (m && KNOWN.has(m[1]!))
      return {
        code: m[1]!,
        message: m[2]!.trim() || m[1]!,
        hint: err && typeof err === 'object' ? (err as { hint?: unknown }).hint : undefined,
      };
    if (/Failed to fetch|NetworkError|Load failed|network request failed/i.test(raw))
      return { code: 'network', message: NETWORK_MESSAGE, detail: raw };
    if (/statement timeout|canceling statement/i.test(raw)) {
      reportOnce(err, raw);
      return { code: 'timeout', message: TIMEOUT_MESSAGE, detail: raw };
    }
    if (/JWT expired|invalid JWT|refresh token/i.test(raw))
      return { code: 'unauthenticated', message: EXPIRED_MESSAGE, detail: raw };
    if (isAuthRefusal(err)) return { code: 'auth', message: raw };
    reportOnce(err, raw);
    return { code: 'unknown', message: UNKNOWN_MESSAGE, detail: raw };
  }
  reportOnce(err, String(err));
  return { code: 'unknown', message: UNKNOWN_MESSAGE, detail: String(err) };
}

/** The sentence to show staff for any error. */
export function errorText(err: unknown): string {
  return toAppError(err).message;
}

/**
 * The error as it is recorded (a command's payload, the audit): the server's own words or the original
 * text, never the plain sentence shown to staff in its place.
 */
export function errorRecord(err: unknown): string {
  const e = toAppError(err);
  return e.detail ?? e.message;
}

export function isNetworkError(err: unknown): boolean {
  return (
    toAppError(err).code === 'network' || (typeof navigator !== 'undefined' && !navigator.onLine)
  );
}
